import { Component } from 'preact';
import { TabBar } from './ui/TabBar';
import { motion } from 'framer-motion';
import AppHeader from './AppHeader';
import { BackButton } from './ui/navButtons';
import AmbientParticles from './AmbientParticles';
import VisibilityStrip from './play/VisibilityStrip';
import SaveList from './play/SaveList';
import PublicBrowser from './play/PublicBrowser';
import TournamentList from './play/TournamentList';
import ReplayHistoryCard from './ReplayHistoryCard';
import {
  VIGNETTE, GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_MUTED,
} from '../lib/medievalTheme';
import { screenPanel } from '../utils/screenEnter';
import {
  listInstances, listPublicInstances, subscribeInstance,
  subscribePublicInstancesChanged,
} from '../utils/arena/instancesApi';
import { listGameSessions, deleteGameSession } from '../utils/game/sessionStorage';
import { getMyTournaments } from '../utils/arena/draftApi';
import { isInstanceRowVisible } from '../utils/arena/instanceVisibility';
import { replayableMatches } from '../utils/arena/matchHistory';
import TutorialOverlay from './TutorialOverlay';
import MobilePlay from './mobile/MobilePlay';
import { isPhoneLayout } from '../utils/platform';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';
import { t, k } from '../localization/t';

const SESSION_TUTORIAL_KEY = 'session-browser-v1';

const SESSION_TUTORIAL_STEPS = [
  {
    key: 'queue',
    title: k('Find a match'),
    body: k('Queue up and get paired with another player. When a match is found, both of you accept and the game starts.'),
    selector: '[data-tutorial="session-visibility-queue"]',
  },
  {
    key: 'solo',
    title: k('Solo'),
    body: k('Play both sides yourself with any two decks. Perfect for testing builds or trying out sandbox setups.'),
    selector: '[data-tutorial="session-visibility-solo"]',
  },
  {
    key: 'friends',
    title: k('Friends'),
    body: k('Start a private match and invite the friends you want. Pick the format when you set it up.'),
    selector: '[data-tutorial="session-visibility-friends"]',
  },
  {
    key: 'public',
    title: k('Public'),
    body: k('Host an open game that any player can join. Anyone browsing Public Games will see it.'),
    selector: '[data-tutorial="session-visibility-public"]',
  },
  {
    key: 'saves',
    title: k('Your Saves'),
    body: k('Resume any session you\'ve paused or left mid-match. Saves live across launches.'),
    selector: '[data-tutorial="session-tab-saves"]',
  },
  {
    key: 'public-games',
    title: k('Public Games'),
    body: k('Browse open matches hosted by other players and jump straight in.'),
    selector: '[data-tutorial="session-tab-public"]',
  },
];

/**
 * Top-level "Play" screen in the instance-first flow. Replaces the
 * mode-tile grid of the old PlayScreen.
 *
 * Layout:
 *   [Header]
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  Play                                                    │
 *   │                                                          │
 *   │  ┌──────────────┐   ┌──────────────────────────────────┐│
 *   │  │ Create New   │   │ ┌────────┬─────────────────────┐ ││
 *   │  │ Instance     │   │ │ Saves  │ Public Browser      │ ││
 *   │  │              │   │ └────────┴─────────────────────┘ ││
 *   │  │  [ Solo  ]   │   │                                  ││
 *   │  │  [ Friends ] │   │  (save rows / public rows)       ││
 *   │  │  [ Public  ] │   │                                  ││
 *   │  │              │   │                                  ││
 *   │  └──────────────┘   └──────────────────────────────────┘│
 *   └──────────────────────────────────────────────────────────┘
 *
 * Left column: three vertically-stacked visibility buttons. Right
 * column: panel-styled instance browser with tabs.
 */
// Finished matches (up to the server's 100-match window), each row opening
// its replay. Searchable by the player-given name or the opponent; rows can
// be renamed inline and pinned to the top. Rows without an archived replay
// render inert.
class PastMatchList extends Component {
  state = { query: '' };

  render() {
    const { onWatchReplay, onUpdateMatchMeta, profile, sorceryCards } = this.props;
    const { query } = this.state;
    const { matches } = this.props;
    const q = query.trim().toLowerCase();
    const visible = q
      ? matches.filter((m) => (m.customName || '').toLowerCase().includes(q)
        || (m.opponentName || '').toLowerCase().includes(q))
      : matches;
    return (
      <div className="flex flex-col gap-0.5">
        <input
          type="text"
          value={query}
          placeholder={t('Search matches...')}
          className="w-full mb-2 px-2.5 py-1.5 rounded-lg text-[0.6875cqw] outline-none"
          style={{
            background: 'rgba(0,0,0,0.3)',
            border: `1px solid ${GOLD} 0.15)`,
            color: TEXT_PRIMARY,
          }}
          onInput={(e) => this.setState({ query: e.currentTarget.value })}
        />
        {visible.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[0.6875cqw] italic py-8" style={{ color: TEXT_MUTED }}>
            {matches.length === 0 ? t('No finished matches yet') : t('No matches found')}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {visible.map((m, i) => (
              <ReplayHistoryCard
                key={`${m.roomCode || 'm'}-${i}`}
                match={m}
                profile={profile}
                sorceryCards={sorceryCards}
                canWatch={!!onWatchReplay}
                onWatch={onWatchReplay}
                onUpdateMatchMeta={onUpdateMatchMeta}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default class SessionBrowser extends Component {
  constructor(props) {
    super(props);
    this.state = {
      instances: [],
      publicInstances: [],
      tournaments: [],
      // Map<instanceId, savedName> populated from local sessions.
      // Surfaced to SaveRow so user-chosen save names ("My Friday
      // draft run") headline the row instead of just "Friends
      // Draft." Host-only. Guests don't own a local blob.
      savedNamesByInstance: new Map(),
      // Set<instanceId> of instances for which we have a local
      // blob. Used to hide "lost save" rows from the list entirely
      // rather than showing dead rows the user can't resume.
      hostLocalBlobIds: new Set(),
      loading: true,
      tab: 'saves',
      showSessionTutorial: false,
    };
    this._instanceSubs = new Map();
  }

  componentDidMount() {
    this.refresh();
    this._pollIv = setInterval(() => this.refresh(), 30_000);
    // Live-refresh the Public Games list the instant the server says
    // it changed (create/join/abandon/start of any public instance),
    // so users don't have to wait up to 30s for the next poll tick.
    this._unsubPublicChanged = subscribePublicInstancesChanged(() => this.refresh());
    // Re-fetch when another surface changed instance state on the
    // server (discard/leave/abandon, resume invites sent). Visibility
    // is a pure function of server state now, so a refresh is the one
    // correct reaction. There is no local filter state to bump.
    this._onInstancesChanged = () => this.refresh();
    window.addEventListener('valkenhall:instances-changed', this._onInstancesChanged);

    const profileId = this.props.profile?.id;
    if (profileId) {
      hydrateTutorialState().then(() => {
        if (this._unmounted) return;
        if (!shouldAutoPlay(profileId, SESSION_TUTORIAL_KEY)) return;
        this._tutorialTimer = setTimeout(() => {
          if (!this._unmounted) this.setState({ showSessionTutorial: true });
        }, 400);
      });
    }
  }

  handleSessionTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, SESSION_TUTORIAL_KEY);
    this.setState({ showSessionTutorial: false });
  };

  componentWillUnmount() {
    clearInterval(this._pollIv);
    for (const off of this._instanceSubs.values()) {
      try { off(); } catch {}
    }
    this._instanceSubs.clear();
    try { this._unsubPublicChanged?.(); } catch {}
    window.removeEventListener('valkenhall:instances-changed', this._onInstancesChanged);
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
    this._unmounted = true;
  }

  refresh = async () => {
    // Load the player's active tournaments from its own endpoint so a failure
    // here can't affect the saves/public lists.
    getMyTournaments()
      .then((tournaments) => { if (!this._unmounted) this.setState({ tournaments }); })
      .catch(() => {});
    try {
      const [instances, publicInstances, localSessions] = await Promise.all([
        listInstances().catch(() => []),
        listPublicInstances().catch(() => []),
        // Local session list is the source for per-instance
        // user-chosen save names. `instanceId` is stamped by
        // _tagWithInstance inside GameBoard on every save.
        listGameSessions().catch(() => []),
      ]);
      if (this._unmounted) return;
      const savedNamesByInstance = new Map();
      const hostLocalBlobIds = new Set();
      // On a dev machine shared by two Electron processes
      // (SESSIONS_DIR is global), filter by userId so a
      // roommate's autosave doesn't mark our instance row as
      // "has blob." userId is stamped by _tagWithInstance.
      const myId = this.props.profile?.id || null;
      // Reap blobs whose match has already been decided. The normal
      // completion path (handleMatchReward) deletes the blob inline,
      // but a crash between /complete and the delete. Or a session
      // that ends without /complete ever firing. Would otherwise
      // leave the bytes on disk forever. Hiding the row via
      // _isRowVisible alone wouldn't actually free the storage.
      const phaseByInstanceId = new Map(
        (instances || []).map((i) => [i.id, i.phase]),
      );
      const orphanSessionIds = [];
      for (const s of localSessions || []) {
        if (!s?.instanceId) continue;
        if (myId && s.userId && s.userId !== myId) continue;
        const phase = phaseByInstanceId.get(s.instanceId);
        if (phase === 'completed' || phase === 'abandoned') {
          orphanSessionIds.push(s.id);
          continue;
        }
        hostLocalBlobIds.add(s.instanceId);
        // Only headline a user-chosen name. The default 'Autosave'
        // placeholder is just noise on the row. The mode + visibility
        // subtitle already identifies the save.
        if (s.name && s.name !== 'Autosave') {
          savedNamesByInstance.set(s.instanceId, s.name);
        }
      }
      // Fire-and-forget. A transient delete failure just gets
      // retried on the next refresh tick.
      for (const sid of orphanSessionIds) {
        deleteGameSession(sid).catch(() => {});
      }
      this.setState({
        instances,
        publicInstances,
        savedNamesByInstance,
        hostLocalBlobIds,
        loading: false,
      });
      this._syncSubscriptions(instances);
    } catch (err) {
      console.error('[SessionBrowser] refresh failed:', err);
      if (!this._unmounted) this.setState({ loading: false });
    }
  };

  _syncSubscriptions(instances) {
    const ids = new Set(instances.map((i) => i.id));
    for (const [id, off] of this._instanceSubs) {
      if (!ids.has(id)) {
        try { off(); } catch {}
        this._instanceSubs.delete(id);
      }
    }
    for (const inst of instances) {
      if (this._instanceSubs.has(inst.id)) continue;
      this._instanceSubs.set(inst.id, () => {});
      subscribeInstance(inst.id, () => this.refresh()).then((off) => {
        if (this._unmounted) { try { off(); } catch {} return; }
        this._instanceSubs.set(inst.id, off);
      }).catch((err) => {
        console.error('[SessionBrowser] subscribe failed:', err);
        this._instanceSubs.delete(inst.id);
      });
    }
  }

  handleVisibilityPick = (visibility) => {
    this.props.onPickVisibility?.(visibility);
  };

  handleRowAction = (instance, action) => {
    this.props.onInstanceAction?.(instance, action);
  };

  handlePublicJoin = (instance) => {
    this.props.onJoinPublic?.(instance);
  };

  // Visibility is the shared server-state predicate. See
  // utils/arena/instanceVisibility.js. No local filter state.
  _isRowVisible = (inst, profileId, blobIds) => (
    isInstanceRowVisible(inst, profileId, blobIds)
  );

  render() {
    const {
      profile, sorceryCards, onBack, onToggleMailbox, mailboxUnreadCount, mailboxDropdown,
      onToggleFriends, friendListData, draftQueueDropdown,
    } = this.props;
    const {
      instances, publicInstances, loading, tab, hostLocalBlobIds,
    } = this.state;
    const profileId = profile?.id;
    const visibleSaves = instances.filter(
      (i) => this._isRowVisible(i, profileId, hostLocalBlobIds),
    );
    const visiblePublic = publicInstances.filter(
      (i) => this._isRowVisible(i, profileId, hostLocalBlobIds),
    );
    const pastMatches = replayableMatches(this.props.matchHistory);

    // Phone: same data + handlers, dedicated rem-sized markup.
    if (isPhoneLayout()) {
      return (
        <MobilePlay
          profile={profile}
          sorceryCards={sorceryCards}
          tab={tab}
          onTab={(t) => this.setState({ tab: t })}
          visibleSaves={visibleSaves}
          visiblePublic={visiblePublic}
          tournaments={this.state.tournaments}
          loading={loading}
          savedNamesByInstance={this.state.savedNamesByInstance}
          onBack={onBack}
          onPickVisibility={this.handleVisibilityPick}
          onRowAction={this.handleRowAction}
          onJoinPublic={this.handlePublicJoin}
          onResumeTournament={this.props.onResumeTournament}
        />
      );
    }

    return (
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{
          background: `url('/play-bg.webp') center/cover no-repeat, #08080a`,
                  }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)' }}
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <AmbientParticles preset="hub" />

        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          draftQueueDropdown={draftQueueDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          zoom={1}
        >
          <BackButton title={t('Back')} onClick={onBack} />
        </AppHeader>

        {/* The outer container is `overflow-hidden`, not scroll.
            all scrolling happens inside the panel body. Otherwise
            switching tabs between short and tall lists would
            toggle the outer scrollbar, and `mx-auto max-w-[92.5cqw]`
            would re-center the entire page (header, buttons, and
            all) by half the scrollbar width. */}
        <div className="relative z-10 flex-1 min-h-0 overflow-hidden">
          {/* pt keeps the panel where the removed chapter-marker header
              used to leave it (header 3.5cqw + gap-6 1.5cqw + pt-8 2cqw). */}
          <div className="mx-auto max-w-[92.5cqw] w-full px-10 pt-[7cqw] pb-8 flex flex-col gap-6 h-full">

            {/* Two-column layout. Left: visibility picker anchors the
                page. Right: the browser panel floats over the play-bg
                as a single cohesive component so the save list reads
                as "one thing" rather than a loose list. */}
            <div
              className="grid gap-6 flex-1 min-h-0"
              style={{ gridTemplateColumns: 'minmax(15cqw, 17.5cqw) minmax(0, 1fr)' }}
            >
              {/* Left. New Session */}
              <motion.aside className="flex flex-col gap-3" {...screenPanel(2)}>
                <div
                  className="text-[0.625cqw] font-semibold px-1 arena-heading"
                  style={{ color: ACCENT_GOLD, textShadow: `0 0 10px ${GOLD} 0.2)` }}
                >
                  {t('New Session')}
                </div>
                <VisibilityStrip onPick={this.handleVisibilityPick} />
              </motion.aside>

              {/* Right. Session browser panel. Frosted-glass look is
                  built from two real child divs, NOT from
                  backdrop-filter (which the CSS minifier strips and
                  which fails under our `zoom` ancestor anyway). The
                  first child is an absolutely-positioned copy of
                  play-bg.webp with `filter: blur(28px)`. This is
                  a pixel filter that always renders. The second is
                  a dark warm tint on top for legibility. Content
                  renders above both via relative positioning. */}
              <motion.section className="vh-forged-panel flex flex-col min-h-0" {...screenPanel(3)}>
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url("/play-bg.webp")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    // `background-attachment: fixed` + `filter: blur()`
                    // under a `zoom` ancestor produces visible smearing
                    // and banding artifacts in Chromium. Scaling the
                    // whole layer up (1.18) + clipping via parent
                    // `overflow: hidden` pushes the blur's soft falloff
                    // past the panel edges so we never see it.
                    transform: 'scale(1.18)',
                    transformOrigin: 'center',
                    // `brightness(0.45)` darkens the blurred image
                    // before it reaches the tint layer, so the panel
                    // stays as dark as the pre-blur version while the
                    // blur softness is still perceivable.
                    filter: 'blur(28px) brightness(0.45) saturate(1.1)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `url("/tex-noise-panel.webp"), linear-gradient(180deg, rgba(14, 11, 8, 0.82) 0%, rgba(8, 6, 4, 0.86) 100%)`,
                    // `soft-light` blends the noise into the gradient
                    // so the texture reads as a subtle surface quality
                    // rather than an overlay stamped on top.
                    backgroundBlendMode: 'soft-light, normal',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />
                {/* Tab bar. Flush with the top edge so the panel
                    reads as one tabbed card. `position: relative +
                    zIndex: 2` keeps it above the blur/tint layers. */}
                <TabBar
                  data-tutorial="session-panel-tabs"
                  className="vh-panel-tabs"
                  variant="chapter"
                  barInset="12%"
                  barStyle={{ bottom: '-0.0625cqw' }}
                  indicatorStyle={{ background: 'linear-gradient(180deg, rgba(212, 168, 67, 0.14) 0%, rgba(100, 70, 20, 0.05) 100%)', boxShadow: 'none' }}
                  activeIndex={['saves', 'public', 'tournaments', 'past'].indexOf(tab)}
                  style={{ zIndex: 2 }}
                >
                  <button
                    type="button"
                    data-tab-item
                    data-tutorial="session-tab-saves"
                    className={`vh-panel-tab ${tab === 'saves' ? 'vh-panel-tab-active' : ''}`}
                    onClick={() => this.setState({ tab: 'saves' })}
                  >
                    {t('Open Matches')}
                    {visibleSaves.length > 0 ? (
                      <span className="vh-panel-tab-count tabular-nums">{visibleSaves.length}</span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    data-tab-item
                    data-tutorial="session-tab-public"
                    className={`vh-panel-tab ${tab === 'public' ? 'vh-panel-tab-active' : ''}`}
                    onClick={() => this.setState({ tab: 'public' })}
                  >
                    {t('Public Games')}
                    {visiblePublic.length > 0 ? (
                      <span className="vh-panel-tab-count tabular-nums">{visiblePublic.length}</span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    data-tab-item
                    className={`vh-panel-tab ${tab === 'tournaments' ? 'vh-panel-tab-active' : ''}`}
                    onClick={() => this.setState({ tab: 'tournaments' })}
                  >
                    {t('Your Tournaments')}
                    {this.state.tournaments.length > 0 ? (
                      <span className="vh-panel-tab-count tabular-nums">{this.state.tournaments.length}</span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    data-tab-item
                    className={`vh-panel-tab ${tab === 'past' ? 'vh-panel-tab-active' : ''}`}
                    onClick={() => this.setState({ tab: 'past' })}
                  >
                    {t('Past Matches')}
                    {pastMatches.length > 0 ? (
                      <span className="vh-panel-tab-count tabular-nums">{pastMatches.length}</span>
                    ) : null}
                  </button>
                </TabBar>

                {/* Panel body. Scrolls independently so the tab bar
                    stays pinned at the top of the card.
                    `scrollbar-gutter: stable` reserves space for the
                    scrollbar at all times so tabs with short vs tall
                    lists don't shift the row layout horizontally. */}
                <div
                  className="flex-1 min-h-0 overflow-y-auto p-4"
                  style={{ scrollbarGutter: 'stable', position: 'relative', zIndex: 2 }}
                >
                  {tab === 'saves' ? (
                    <SaveList
                      instances={visibleSaves}
                      loading={loading}
                      currentUserId={profileId}
                      sorceryCards={sorceryCards}
                      savedNamesByInstance={this.state.savedNamesByInstance}
                      onAction={this.handleRowAction}
                    />
                  ) : tab === 'past' ? (
                    <PastMatchList
                      matches={pastMatches}
                      profile={profile}
                      sorceryCards={sorceryCards}
                      onWatchReplay={this.props.onWatchReplay}
                      onUpdateMatchMeta={this.props.onUpdateMatchMeta}
                    />
                  ) : tab === 'tournaments' ? (
                    <TournamentList
                      tournaments={this.state.tournaments}
                      loading={loading}
                      onResume={this.props.onResumeTournament}
                    />
                  ) : (
                    <PublicBrowser
                      instances={visiblePublic}
                      currentUserId={profileId}
                      currentUserTier={profile?.rank?.tier}
                      sorceryCards={sorceryCards}
                      onJoin={this.handlePublicJoin}
                    />
                  )}
                </div>
              </motion.section>
            </div>
          </div>
        </div>
        {this.state.showSessionTutorial && (
          <TutorialOverlay
            steps={SESSION_TUTORIAL_STEPS.map((s) => ({ ...s, title: t(s.title), body: t(s.body) }))}
            onDismiss={this.handleSessionTutorialDismiss}
          />
        )}
      </div>
    );
  }
}
